import React, { Suspense } from 'react';
import {
  ForgotPassword,
  Maintenance,
  MyProfile,
  NotFound,
  PendingApproval,
  PendingPayment,
  RedirectPage,
  ResetPassword,
  SmsVerification,
  CurrentAccount,
} from '@src/pages';
import PropTypes from 'prop-types';
import Survey from './pages/Survey';
import * as TicketRestaurant from '@src/pages/ticketRestaurant';
import * as TicketCar from '@src/pages/ticketCar';
import * as TicketCompliments from '@src/pages/ticketCompliments';
import * as TicketMarket from '@src/pages/ticketMarket';
import { Redirect, Route, Switch } from 'react-router-dom';
import * as CompanyInformation from '@src/pages/companyInformation';
import PrivateRoute from './authentication/PrivateRoute';
import HomeLayout from '@src/layout/HomeLayout';
import { Loading } from '@src/components';
import Dev from '@src/pages/Dev';

const AdminRouter = React.lazy(() => import('@src/pages/admin/AdminRouter'));
const Login = React.lazy(() => import('@src/pages/login'));
const PreLogin = React.lazy(() => import('@src/pages/preLogin'));
const Dashboard = React.lazy(() => import('@src/pages/dashboard'));
const matchPropType = PropTypes.shape({
  path: PropTypes.string.isRequired,
}).isRequired;

function getComponent() {
  return () => <Redirect to={{ pathname: '/404', state: { status: 404 } }} />;
}

function getComponent1() {
  return ({ match }) => {
    return (
      <Suspense fallback={<Loading />}>
        <AdminRouter />
      </Suspense>
    );
  };
}

function getComponent2() {
  const TicketRestaurantRouter = ({ match }) => {
    return (
      <Switch>
        <PrivateRoute
          path={`${match?.path}/balance-upload`}
          Component={TicketRestaurant?.BalanceUpload}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/card-orders`}
          exact
          Component={TicketRestaurant?.CardOrders}
          authority="dashboard"
        />
        <PrivateRoute
          exact
          path={`${match?.path}/my-orders`}
          Component={TicketRestaurant?.MyOrders}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/my-orders/delivery-confirmation`}
          Component={TicketRestaurant?.DeliveryConfirmation}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/draft-order`}
          exact
          Component={TicketRestaurant?.DraftOrder}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/draft-order/card-orders/:cardOrderId`}
          Component={TicketRestaurant?.CardOrders}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/draft-order/balance-upload/:balanceId`}
          Component={TicketRestaurant?.BalanceUpload}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/manage-my-cards`}
          Component={TicketRestaurant?.ManageMyCards}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/auto-upload-instructions`}
          Component={TicketRestaurant?.AutoUploadInstructions}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/auto-upload-instructions/new-instruction`}
          Component={TicketRestaurant?.NewInstruction}
          authority="dashboard"
        />

        <PrivateRoute
          path={`${match?.path}/auto-upload-instructions/edit-instruction`}
          Component={TicketRestaurant?.NewInstruction}
          authority="dashboard"
        />

        <PrivateRoute
          path={`${match?.path}/auto-upload-instructions/confirm-instruction`}
          Component={TicketRestaurant?.ConfirmInstruction}
          authority="dashboard"
        />

        <PrivateRoute
          path={`${match?.path}/current-account-report`}
          Component={CurrentAccount}
          authority="dashboard"
        />

        <PrivateRoute
          path={`${match?.path}/manage-my-cards/balance-transfer`}
          Component={TicketRestaurant?.BalanceTransfer}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/ba-bs`}
          Component={TicketRestaurant?.BaBs}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/my-invoices`}
          Component={TicketRestaurant?.MyInvoices}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/balance-report`}
          Component={TicketRestaurant?.BalanceReport}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/balance-transfer-report`}
          Component={CompanyInformation?.BalanceTransferReport}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/uploaded-balance-report`}
          Component={CompanyInformation?.UploadedBalanceReport}
          authority="dashboard"
          exact
        />
        <Route component={getComponent()} />
      </Switch>
    );
  };

  TicketRestaurantRouter.propTypes = {
    match: matchPropType,
  };

  return TicketRestaurantRouter;
}

function getComponent3() {
  const ticketGiftRouter = ({ match }) => {
    return (
      <Switch>
        <PrivateRoute
          path={`${match?.path}/balance-upload`}
          Component={TicketCompliments?.BalanceUpload}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/uploaded-balance-report`}
          exact
          Component={TicketCompliments?.TGUploadedBalanceReports}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/balance-report`}
          Component={TicketCompliments?.BalanceGiftReport}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/card-orders`}
          exact
          Component={TicketCompliments?.CardOrders}
          authority="dashboard"
        />
        <PrivateRoute
          exact
          path={`${match?.path}/my-orders`}
          Component={TicketCompliments?.MyOrders}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/draft-order`}
          exact
          Component={TicketCompliments?.DraftOrder}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/draft-order/card-orders/:cardOrderId`}
          Component={TicketCompliments?.CardOrders}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/draft-order/balance-upload/:balanceId`}
          Component={TicketCompliments?.BalanceUpload}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/manage-my-cards`}
          Component={TicketCompliments?.ManageGiftCards}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/auto-upload-instructions`}
          Component={TicketCompliments?.AutoUploadInstructions}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/manage-my-cards/balance-transfer`}
          Component={TicketCompliments?.BalanceTransfer}
          authority="dashboard"
          exact
        />

        <PrivateRoute
          path={`${match?.path}/gift-invoices`}
          Component={TicketCompliments?.GiftMyInvoices}
          authority="dashboard"
          exact
        />
        <Route component={getComponent()} />
      </Switch>
    );
  };
  ticketGiftRouter.propTypes = {
    match: matchPropType,
  };

  return ticketGiftRouter;
}

function getComponentTM() {
  const ticketMarketRouter = ({ match }) => {
    return (
      <Switch>
        <PrivateRoute
          path={`${match?.path}/card-orders`}
          exact
          Component={TicketMarket?.CardOrders}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/balance-upload`}
          Component={TicketMarket?.BalanceUpload}
          authority="dashboard"
        />
        <PrivateRoute
          exact
          path={`${match?.path}/my-orders`}
          Component={TicketMarket?.MyOrders}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/manage-my-cards`}
          Component={TicketMarket?.ManageGiftCards}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/my-invoices`}
          Component={TicketMarket?.GiftMyInvoices}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/current-account-report`}
          Component={TicketMarket?.CurrentAccount}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/uploaded-balance-report`}
          exact
          Component={TicketMarket?.TGUploadedBalanceReports}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/balance-report`}
          Component={TicketMarket?.BalanceMarketReport}
          authority="dashboard"
          exact
        />
        {/* 
        <PrivateRoute
          path={`${match?.path}/draft-order`}
          exact
          Component={TicketMarket?.DraftOrder}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/draft-order/card-orders/:cardOrderId`}
          Component={TicketMarket?.CardOrders}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/draft-order/balance-upload/:balanceId`}
          Component={TicketMarket?.BalanceUpload}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/auto-upload-instructions`}
          Component={TicketMarket?.AutoUploadInstructions}
          authority="dashboard"
          exact
        />
        <PrivateRoute
          path={`${match?.path}/manage-my-cards/balance-transfer`}
          Component={TicketMarket?.BalanceTransfer}
          authority="dashboard"
          exact
        />
        */}
        <Route component={getComponent()} />
      </Switch>
    );
  };
  ticketMarketRouter.propTypes = {
    match: matchPropType,
  };

  return ticketMarketRouter;
}

function getComponent4() {
  const CompanyInformationRouter = ({ match }) => {
    return (
      <Switch>
        <PrivateRoute
          path={`${match?.path}/delivery-adresses`}
          Component={CompanyInformation?.DeliveryAddresses}
          authority="dashboard"
        />
        <PrivateRoute
          path={`${match?.path}/general-company-information`}
          Component={CompanyInformation?.GeneralCompanyInformation}
          authority="dashboard"
        />

        <PrivateRoute
          path={`${match?.path}/management-user`}
          Component={CompanyInformation?.ManagementUser}
          authority="dashboard"
          exact
        />

        <PrivateRoute
          path={`${match?.path}/management-user/new-user`}
          Component={CompanyInformation?.NewUser}
          authority="dashboard"
        />

        <PrivateRoute
          path={`${match?.path}/management-user/edit-user`}
          Component={CompanyInformation?.EditUser}
          authority="dashboard"
        />

        <PrivateRoute
          path={`${match?.path}/credit-cards`}
          Component={CompanyInformation?.CreditCards}
          authority="dashboard"
          exact
        />

        <PrivateRoute
          path={`${match?.path}/credit-cards/new-credit-card`}
          Component={CompanyInformation?.NewCreditCard}
          authority="dashboard"
        />

        <Route component={getComponent()} />
      </Switch>
    );
  };

  CompanyInformationRouter.propTypes = {
    match: matchPropType,
  };

  return CompanyInformationRouter;
}

const getComponent5 = ({ match }) => {
  return (
    <Switch>
      <PrivateRoute
        path={`${match?.path}/add-car`}
        Component={TicketCar?.AddNewCar}
        authority="dashboard"
      />
      <PrivateRoute
        path={`${match?.path}/vehicle-management`}
        Component={TicketCar?.VehicleManagement}
        authority="dashboard"
        exact
      />
      <PrivateRoute
        path={`${match?.path}/vehicle-management/:id`}
        Component={TicketCar?.VehicleManagementDetail}
        authority="dashboard"
        exact
      />
      <PrivateRoute
        path={`${match?.path}/add-fleet-limit`}
        Component={TicketCar?.AddFleetLimit}
        authority="dashboard"
        exact
      />
      <PrivateRoute
        path={`${match?.path}/3`}
        Component={TicketCar?.Demands}
        authority="dashboard"
        exact
      />
      <PrivateRoute
        path={`${match?.path}/2`}
        Component={TicketCar?.NewCarDemands}
        authority="dashboard"
        exact
      />
      <PrivateRoute
        path={`${match?.path}/fuel-consumption`}
        Component={TicketCar?.FuelConsumption}
        authority="dashboard"
        exact
      />
      <PrivateRoute
        path={`${match?.path}/car-invoices`}
        Component={TicketCar?.CarInvoices}
        authority="dashboard"
        exact
      />
      <PrivateRoute
        path={`${match?.path}/cost-center`}
        Component={TicketCar?.CostCenter}
        authority="dashboard"
        exact
      />
      <PrivateRoute
        path={`${match?.path}/departments`}
        Component={TicketCar?.Departments}
        authority="dashboard"
        exact
      />
      <Route component={() => <Redirect to={{ pathname: '/404', state: { status: 404 } }} />} />
    </Switch>
  );
};

const Router = () => {
  return (
    <Switch>
      <Route exact path="/login" component={Login} />
      <Route exact path="/forgot-password" component={ForgotPassword} />
      <Route exact path="/reset-password" component={ResetPassword} />
      <Route exact path="/sms-verification" component={SmsVerification} />
      <Route exact path="/404" component={NotFound} />
      <Route exact path="/502" component={Maintenance} />
      <Route exact path="/survey-approved" component={Survey} />
      <Route exact path="/survey-rejected" component={Survey} />
      <Route exact path={'/redirect'} component={RedirectPage} />
      <Route exact path={'/pre-login'} component={PreLogin} />

      <Route path={'/admin'} component={getComponent1()} authority="dashboard" isLayout={false} />
      <HomeLayout>
        <PrivateRoute exact path={'/'} Component={Dashboard} authority="dashboard" />
        <PrivateRoute exact path={'/dashboard'} Component={Dashboard} authority="dashboard" />
        <PrivateRoute exact path={'/dev'} Component={Dev} authority="dashboard" />
        <PrivateRoute path={'/profile'} Component={MyProfile} authority="dashboard" />
        <PrivateRoute
          path={'/pending-approval'}
          Component={PendingApproval}
          authority="dashboard"
        />
        <PrivateRoute path={'/pending-payment'} Component={PendingPayment} authority="dashboard" />
        <PrivateRoute
          path={'/ticket-restaurant'}
          Component={getComponent2()}
          authority="dashboard"
          isLayout={false}
        />
        <PrivateRoute
          path={'/ticket-car'}
          Component={getComponent5}
          authority="dashboard"
          isLayout={false}
        />

        <PrivateRoute
          path={'/ticket-gift'}
          Component={getComponent3()}
          authority="dashboard"
          isLayout={false}
        />

        <PrivateRoute
          path={'/ticket-market'}
          Component={getComponentTM()}
          authority="dashboard"
          isLayout={false}
        />

        <PrivateRoute
          path={'/company-information'}
          Component={getComponent4()}
          authority="dashboard"
          isLayout={false}
        />
      </HomeLayout>

      <Route component={getComponent()} />
    </Switch>
  );
};

getComponent5.propTypes = {
  match: matchPropType,
};
export default Router;
